import { Component, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { FiaService } from 'src/app/services/fia.service';
import { MediaService } from 'src/app/services/media.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UserService } from 'src/app/services/user.service';
import { TagsService } from 'src/app/services/tags.service';
import { LookupFiaStatus } from 'src/app/shared/models/general/lookup-fia-status';
import { LookupMediaTypes } from 'src/app/shared/models/general/lookup-media-types';
import { Media } from 'src/app/shared/models/general/media';
import { FiaApplication } from 'src/app/shared/models/users/fia-application';
import { UserFullDetails } from 'src/app/shared/models/users/user-full-details';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { Users } from 'src/app/shared/models/users/users';
import { UsersSearchRequest } from 'src/app/shared/models/users/users-search-request';
import { UserSearchResult } from 'src/app/shared/models/users/user-search-result';
import { UserFiaPins } from 'src/app/shared/models/users/user-fia-pins';
import { Error } from 'src/app/shared/models/general/error';
import { Tag } from 'src/app/shared/models/general/tag';
import { ExcelExportService } from 'src/app/services/excel-export.service';

@Component({
  selector: 'app-user-management',
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss'],
})
export class UserManagementComponent {
  //Modals
  showUserProfileModal: boolean = false;
  showUserFiaApplicationsModal: boolean = false;
  showUserFiaApplicationModal: boolean = false;
  showUserFiaDocumentsModal: boolean = false;
  showNewUserModal: boolean = false;
  showUserDocumentsModal: boolean = false;
  showUserFiaPinsModal: boolean = false;
  showUserFiaPinModal: boolean = false;

  showUserTradeHistoryModal: boolean = false;
  showUserSummaryModal: boolean = false;
  showCreateNewTradeModal: boolean = false;
  selectedUserTags: Tag[] = [];

  roleId: number = 0;

  //File selector
  @ViewChild('file') file;

  busy: boolean = true;
  modalBusy: boolean = false;
  showError: boolean = false;
  errorMsg: string = 'Oops! Looks like something went wrong, please contact support if this issue persists';

  //General
  users: UserFullDetails[] = [];
  fiaStatus: LookupFiaStatus[] = [];
  mediaTypes: LookupMediaTypes[] = [];
  usersSearchRequest: UsersSearchRequest = new UsersSearchRequest();
  usersSearchResult: UserSearchResult[] = [];

  searchResult: UserSearchResult;

  //Selected User
  selectedUserFullDetails: UserFullDetails = new UserFullDetails();
  selectedUser: Users = new Users();
  selectedUserProfile: UserProfile = new UserProfile();
  selectedUserFiaApplcations: FiaApplication[] = [];
  selectedUserFiaDocuments: Media[] = [];
  selectedUserDocuments: Media[] = [];
  selectedUserFiaPins: UserFiaPins[] = [];

  //Tag filters
  originalUserList: UserSearchResult[] = [];
  selectedTagFilterId: number = 0;
  tagFilter: Tag[] = [];

  constructor(
    private router: Router,
    public settingsService: SettingsService,
    public usersService: UserService,
    public fiaService: FiaService,
    public mediaService: MediaService,
    public modalService: NgbModal,
    private tagService: TagsService,
    private excelExportService: ExcelExportService
  ) {
    this.busy = true;
    this.settingsService.ValidateGlobalVars().then(() => {
      this.settingsService.GetLookupFiaStatus().then(() => {
        this.fiaStatus = this.settingsService.fiaStatus;
        this.settingsService.GetLookupMediaTypes().then(() => {
          this.mediaTypes = this.settingsService.mediaTypes;

          this.subscribeToTagsRefreshStream();
          if (!this.settingsService.userIsBroker) {
            //Redirect to login
            this.router.navigate(['/login']);
          }

          this.usersSearchRequest.entityType = this.settingsService.brokerEntity.id;
          this.usersSearchRequest.entityId = this.settingsService.brokerUsers[0].brokerId;

          this.roleId = this.settingsService.userRoles[0].roleId;

          this.SearchUsers();
        });
      });
    });
  }

  ExportToExcel() {
    const exportData = this.usersSearchResult.map((user) => ({
      'User Id': user.id,
      'Full Name': user.fullName,
      'Email': user.email,
      'SA ID Number': user.idNumber,
      'Cellphone': user.mobile,
    }));
    this.excelExportService.exportFromJson(exportData, 'UserManagement_');
  }

  SelectUser() {
    this.selectedUserFiaDocuments = [];
    this.selectedUserProfile = this.selectedUserFullDetails.profile;
    this.selectedUserFiaApplcations = this.selectedUserFullDetails.fiaApplications;
    this.selectedUser = this.selectedUserFullDetails.user;
  }

  SearchUsers() {
    this.busy = true;
    this.usersService.SearchUsers(this.usersSearchRequest).subscribe(
      (userSearchResults: UserSearchResult[]) => {
        this.originalUserList = userSearchResults;
        this.usersSearchResult = userSearchResults;
        this.FillTagFilter();
        this.busy = false;
        this.showError = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      }
    );
  }

  GetUserFullDetails(userId: number, profileOnly: boolean = true) {
    return new Promise((resolve, reject) => {
      this.modalBusy = true;
      this.usersService.GetUserDetails(userId, profileOnly).subscribe({
        next: (response) => {
          this.selectedUserFullDetails = response;
          this.SelectUser();
          this.modalBusy = false;
          this.showError = false;
          resolve(true);
        },
        error: (err) => {
          reject(err);
        },
      });
    });
  }

  GetUsersFiaPins(userId: number) {
    return new Promise((resolve) => {
      this.modalBusy = true;
      this.usersService.GetUsersFiaPins(userId, false).subscribe(
        (response) => {
          this.selectedUserFiaPins = response;
          this.modalBusy = false;
          resolve(true);
        },
        (error) => {
          this.showError = true;
          this.errorMsg = error.error.ErrorDetail;
          this.modalBusy = false;
          resolve(true);
        }
      );
    });
  }

  GetFiaSupportingDocuments() {
    this.busy = true;
    this.mediaService
      .GetFiaSupportingDocuments(this.selectedUserProfile.userId, this.settingsService.fiaSupportingMediaTypes[1].Id)
      .subscribe(
        (response) => {
          this.selectedUserFiaDocuments = response;
          this.busy = false;
        },
        (error) => {
          this.showError = true;
          this.errorMsg = error.error.ErrorDetail;
          this.busy = false;
        }
      );
  }

  GetUserDocuments() {
    this.busy = true;
    this.mediaService.GetMediaByEntityType(this.settingsService.userEntity.description, this.selectedUserProfile.userId).subscribe(
      (response) => {
        this.selectedUserDocuments = response;
        this.busy = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      }
    );
  }
  //TODO: We need to look at this.
  //Each time GetUserFullDetails is called which is unnecessary as it is set earlier.
  //TODO: GetFiaApplications needs to be split out so that only gets called when needed. Perhaps inside the component?
  //TODO: GetFiaPins needs to be split out out so that only gets called when needed. Perhaps inside the component?
  async ShowModal(userId: number, Modal: string) {
    this.modalBusy = true;
    try {
      if (Modal == 'userInfoModal') {
        await this.GetUserFullDetails(userId);
        this.showUserProfileModal = true;
      } else if (Modal == 'userFiaApplicationsModal') {
        await this.GetUserFullDetails(userId, false);
        const foundIndex = this.usersSearchResult.findIndex((x) => x.id === userId);
        this.selectedUserTags = this.usersSearchResult[foundIndex].tags;
        this.showUserFiaApplicationsModal = true;
      } else if (Modal == 'userFiaDocumentsModal') {
        await this.GetUserFullDetails(userId);
        this.GetFiaSupportingDocuments();
        this.showUserFiaDocumentsModal = true;
      } else if (Modal == 'userDocumentsModal') {
        await this.GetUserFullDetails(userId);
        this.GetUserDocuments();
        this.showUserDocumentsModal = true;
      } else if (Modal == 'userFiaApplicationModal') {
        await this.GetUserFullDetails(userId, false);
        this.showUserFiaApplicationModal = true;
      } else if (Modal == 'userFiaPinsModal') {
        await this.GetUserFullDetails(userId);
        await this.GetUsersFiaPins(userId);
        this.showUserFiaPinsModal = true;
      } else if (Modal == 'userFiaPinModal') {
        await this.GetUserFullDetails(userId);
        this.showUserFiaPinModal = true;
      } else if (Modal == 'newUserModal') {
        this.showNewUserModal = true;
      } else if (Modal == 'tradeSummary') {
        await this.GetUserFullDetails(userId);
        this.showUserSummaryModal = true;
      } else if (Modal == 'tradeHistory') {
        await this.GetUserFullDetails(userId);
        this.showUserTradeHistoryModal = true;
      }
    } catch (error) {
      this.parseAndDisplayError(error);
    } finally {
      this.modalBusy = false;
    }
  }

  ShowModalPopUp(user: UserSearchResult, modalName: string) {
    this.modalBusy = true;
    switch (modalName) {
      case 'createNewTradeModal': {
        this.showCreateNewTradeModal = true;
        this.searchResult = user;
        break;
      }
      default: {
        //statements;
        break;
      }
    }
    this.modalBusy = false;
  }

  HideModal() {
    this.selectedUserFiaDocuments = [];

    this.showUserProfileModal = false;
    this.showUserFiaApplicationsModal = false;
    this.showUserFiaDocumentsModal = false;
    this.showUserDocumentsModal = false;
    this.showUserFiaApplicationModal = false;
    this.showNewUserModal = false;
    this.showUserFiaPinsModal = false;
    this.showUserFiaPinModal = false;
    this.showUserSummaryModal = false;
    this.showUserTradeHistoryModal = false;
  }

  private subscribeToTagsRefreshStream() {
    this.tagService.refreshNeeded$.subscribe((userId) => {
      this.tagService.GetUserTags(userId, true).subscribe({
        next: (userTags: Tag[]) => {
          this.updateTagsForUser(userTags, userId);
        },
        error: (error) => {
          this.errorMsg = Error.InterpretErrorMessage(error);
          this.showError = true;
        },
      });
    });
  }

  public filterByTagId() {
    if (this.selectedTagFilterId === 0) {
      this.usersSearchResult = [...this.usersSearchResult];
      return;
    }

    this.usersSearchResult = this.originalUserList.filter((x) => {
      return x.tags.some((tag) => tag.id === this.selectedTagFilterId);
    });
  }

  private updateTagsForUser(updatedTags: Tag[], userId: number) {
    const foundIndex = this.usersSearchResult.findIndex((x) => x.id === userId);

    this.usersSearchResult[foundIndex].tags = updatedTags;
  }

  FillTagFilter() {
    this.tagService.GetAllTags().subscribe({
      next: (tagFilterList: Tag[]) => {
        this.tagFilter = [];
        const allTag = new Tag();
        allTag.id = 0;
        allTag.name = 'All';
        this.tagFilter.push(allTag);
        this.tagFilter.push(...tagFilterList);
      },
      error: (error) => {
        this.errorMsg = `Failed to fill tag filter list -  ${Error.InterpretErrorMessage(error)}`;
        this.showError = true;
      },
    });
  }

  trackById(index: number, item: any) {
    return item?.id || index;
  }

  parseAndDisplayError(error: any) {
    this.errorMsg = Error.InterpretErrorMessage(error);
    this.showError = true;
    this.modalBusy = false;
  }
}
